import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { PHONE_TILT } from '@erectus/shared';
import type { FrameRecord, SessionSummary } from '@erectus/shared';
import { canOptInToDataSharing, useAuth } from '@/stores/auth.js';
import { track } from '@/lib/analytics.js';
import { Replay } from './Replay.js';
import { defaultSpeed, measurable, type ReplaySpeed } from './replayModel.js';
import { hasSharedFrames, loadSharedFrames } from './sharedFrames.js';

/**
 * Where a session's replay comes from:
 *  - 'device'  — the frames on the phone that recorded it (kept 7 days);
 *  - 'account' — the user's own shared keypoint file, when they opted in to
 *                detection-improvement sharing (CLAUDE.md principle 3). This is
 *                what makes a replay open on another device.
 * Shared files are fetched only when the replay is opened, not with the report.
 */
export type ReplaySource = 'device' | 'account';

export interface ReplayControl {
  source: ReplaySource | null;
  speed: ReplaySpeed;
  loading: boolean;
  error: string | null;
  open(from: 'button' | 'snapshot', t: number): void;
  /** Frames from the account file once fetched (by `open` or `prefetch`). */
  sharedFrames: FrameRecord[] | null;
  /** Fetch the account file without opening the replay — for the snapshot cards on another device. */
  prefetch(): void;
  /** The full-screen replay while it is open; render it anywhere. */
  element: ReactNode;
}

export function useReplay(sessionId: string, summary: SessionSummary, frames: readonly FrameRecord[]): ReplayControl {
  const { user, userDoc } = useAuth();
  const onDevice = frames.some((f) => measurable(f.state) && f.lm.length > 0);
  const optedIn = userDoc?.consent?.dataOptIn === true;
  const [shared, setShared] = useState(false);
  const [sharedFrames, setSharedFrames] = useState<FrameRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openAt, setOpenAt] = useState<{ t: number; autoPlay: boolean } | null>(null);
  const speed = defaultSpeed(summary.durationSec * 1000);
  // Level the skeleton the way the live pipeline levelled the angles.
  const roll = summary.phoneRollDeg ?? 0;
  const rollDeg = Math.abs(roll) <= PHONE_TILT.maxRollDeg ? roll : 0;

  useEffect(() => {
    if (onDevice || !optedIn || !user) return;
    let cancelled = false;
    void hasSharedFrames(user.uid, sessionId).then((exists) => {
      if (!cancelled) setShared(exists);
    });
    return () => {
      cancelled = true;
    };
  }, [onDevice, optedIn, user, sessionId]);

  const source: ReplaySource | null = onDevice ? 'device' : shared ? 'account' : null;

  const fetchShared = useCallback(
    (then?: () => void) => {
      if (!user) return;
      // loadSharedFrames shares one download per session, so a tap while the
      // snapshot prefetch is still running joins it instead of being dropped.
      setLoading(true);
      setError(null);
      loadSharedFrames(user.uid, sessionId, rollDeg)
        .then((loaded) => {
          setSharedFrames(loaded);
          then?.();
        })
        .catch(() => setError('Could not load the replay. Check your connection and try again.'))
        .finally(() => setLoading(false));
    },
    [user, sessionId, rollDeg],
  );

  const open = useCallback(
    (from: 'button' | 'snapshot', t: number) => {
      if (!source) return;
      const show = (): void => {
        setOpenAt({ t, autoPlay: from === 'button' });
        track('replay_open', { from, speed: `${speed}x`, source });
      };
      if (source === 'device' || sharedFrames) {
        show();
        return;
      }
      fetchShared(show);
    },
    [source, sharedFrames, fetchShared, speed],
  );

  const prefetch = useCallback(() => {
    if (source === 'account' && !sharedFrames) fetchShared();
  }, [source, sharedFrames, fetchShared]);

  const replayFrames = source === 'device' ? frames : sharedFrames;
  const element =
    openAt && replayFrames ? (
      <Replay
        frames={replayFrames}
        episodes={summary.episodes}
        startedAt={summary.startedAt}
        rollDeg={rollDeg}
        startAt={openAt.t}
        autoPlay={openAt.autoPlay}
        initialSpeed={speed}
        onClose={() => setOpenAt(null)}
      />
    ) : null;

  return { source, speed, loading, error, open, sharedFrames, prefetch, element };
}

const NUDGE_DISMISSED_KEY = 'erectus.replayShareNudgeDismissed';

function nudgeDismissed(): boolean {
  try {
    return localStorage.getItem(NUDGE_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * A quiet, dismissible pointer to the detection-improvement opt-in, shown
 * next to replays (LEGAL §3). It states facts — this phone keeps 7 days,
 * shared sessions live in the account — and leads with what sharing is for.
 * Every session replays on its phone for 7 days, opted in or not; the choice
 * itself is made at sign-up or on the opt-in screen.
 */
export function ShareNudge(): ReactNode {
  const { userDoc } = useAuth();
  const [dismissed, setDismissed] = useState(nudgeDismissed);
  const eligible = canOptInToDataSharing(userDoc?.profile?.birthYear);
  if (dismissed || !eligible || !userDoc?.consent || userDoc.consent.dataOptIn) return null;

  const dismiss = (): void => {
    setDismissed(true);
    track('data_optin_prompt', { action: 'dismiss' });
    try {
      localStorage.setItem(NUDGE_DISMISSED_KEY, '1');
    } catch {
      // Private mode: it simply shows again next time.
    }
  };

  return (
    <div className="space-y-2 rounded-xl bg-surface p-3 text-xs text-ink-soft">
      <p>
        This phone keeps replays for 7 days. If you choose to share keypoint data to help improve
        detection, your sessions stay in your account — replay them any time, on any device. Optional,
        and you can stop any time.
      </p>
      <div className="flex gap-4">
        <Link
          to="/settings/data"
          onClick={() => track('data_optin_prompt', { action: 'open' })}
          className="font-semibold text-ink underline"
        >
          See what is shared
        </Link>
        <button type="button" onClick={dismiss} className="text-muted">
          Not now
        </button>
      </div>
    </div>
  );
}
